import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '../common/Button';
import FullBlockingModal from '../common/FullBlockingModal';
import { useAllianceStore } from '../../stores/allianceStore';
import { UserAlliance } from '../../types/alliance';

interface AllianceConfirmDeleteModalProps {
  alliance: UserAlliance;
  onClose: () => void;
}

const AllianceConfirmDeleteModal = ({ alliance, onClose }: AllianceConfirmDeleteModalProps) => {
  const { t } = useTranslation(['alliances', 'common']);
  const { retireAlliance } = useAllianceStore();
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleDelete = async () => {
    try {
      setDeleting(true);
      setError(null);

      const result = await retireAlliance(alliance.alliance_id);
      if (!result.success) throw new Error('Delete failed');
      onClose();
    } catch (error) {
      console.error(error);
      setError(
        t('allianceRequestCancelledError', {
          ns: 'alliances',
          defaultValue: 'Erreur lors de la suppression.',
        })
      );
    } finally {
      setDeleting(false);
    }
  };

  return (
    <FullBlockingModal innerClassName="md:max-w-[48rem]">
      <button
        className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 text-2xl md:top-0"
        onClick={onClose}
        aria-label={t('common:form_actions.close')}
      >
        &times;
      </button>

      <h2 className="text-2xl font-bold text-secondary mb-4">
        {t('confirmDeleteTitle', { ns: 'alliances', defaultValue: 'Confirmer la suppression' })}
      </h2>

      <p className="text-secondary text-[0.9375rem] font-ambit mb-6">
        {t('confirmDeleteMessage', {
          ns: 'alliances',
          defaultValue:
            'Voulez-vous vraiment supprimer votre alliance avec {{name}} {{last_name}} ?',
          name: `${alliance.user.first_name} ${alliance.user.last_name}`,
        })}
      </p>

      {error && <div className="text-[#F04438] mb-4">{error}</div>}

      <div className="flex gap-4 flex-col md:flex-row-reverse md:items-center">
        <Button
          className="w-full md:w-auto md:px-8 bg-red-600 text-white"
          onClick={handleDelete}
          disabled={deleting}
        >
          {deleting
            ? t('deleting', { ns: 'alliances', defaultValue: 'Suppression...' })
            : t('confirmDeleteButton', { ns: 'alliances', defaultValue: 'Supprimer' })}
        </Button>

        <Button
          className="w-full md:w-auto md:px-8"
          variant="transparent-with-border"
          onClick={onClose}
        >
          {t('common:form_actions.cancel')}
        </Button>
      </div>
    </FullBlockingModal>
  );
};

export default AllianceConfirmDeleteModal;
